import { defineStore } from "pinia";
import { UsersApi } from "@simbelapp/order-sdk";
import { useAuthStore } from "../auth/auth.store";
import { useUsersValidation } from "~/composables/users/useUsers";
import type { ICreateUsers } from "~/utils/interfaces/users-interfaces";
import { useCompaniesStore } from "~/store/companies/companies.store";
import { CompanyIdentificationModeEnum } from "~/utils/enums/companies.enums";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";

export const useCreateUserStore = defineStore("useCreateUserStore", {
  state: (): ICreateUsers => {
    return {
      users: {
        emailsList: null,
        role: null,
        legalEntity: null,
        manager: null,
        language: null,
        ssoDisabled: false,
        employeesNumbers: null,
      },
      toggleEmail: true,
      showCreateModal: false,
      emailsListError: null,
      roleError: null,
      employeesNumberListError: null,
    };
  },
  getters: {},
  actions: {
    openCreateUserModal() {
      this.$reset();
      this.showCreateModal = true;
    },

    async validateUserCreation() {
      const usersValidation = useUsersValidation();
      const t$ = await usersValidation.validateUsersCreation();
      if (t$?.value && t$.value?.$errors) {
        const companiesStore = useCompaniesStore();
        if (companiesStore.companyInfos?.identificationMode === CompanyIdentificationModeEnum.EMAIL) {
          this.emailsListError = t$.value.emailsList?.$silentErrors.map((m: any) => m.$message).join("");
        }
      }
    },

    async createUsers() {
      const companiesStore = useCompaniesStore();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
      const authStore = useAuthStore();
      let emails;
      let employeesNumbers;

      const isEmailMode = companiesStore.companyInfos?.identificationMode === CompanyIdentificationModeEnum.EMAIL;

      // Clear errors from a previous submit so a retry starts clean.
      this.emailsListError = null;
      this.employeesNumberListError = null;

      if (isEmailMode) {
        if (this.users.emailsList) emails = this.extractEmailsFromText(this.users.emailsList!)!;
      } else if (this.users.employeesNumbers)
        employeesNumbers = this.users.employeesNumbers?.split(/,| +|;/).filter((item) => item !== "");

      if (
        authStore.user?.mainEntityId?.companyId &&
        this.users.legalEntity?.companyId &&
        this.users?.role &&
        this.users?.language
      ) {
        try {
          const data = await apiInstance.createUsers({
            createUsersBatchDto: {
              emails,
              mainEntityId: authStore.user?.mainEntityId?.companyId,
              managerId: this.users.manager?.userId,
              legalEntityId: this.users.legalEntity?.companyId,
              roleName: this.users?.role,
              language: this.users?.language ?? "fr",
              toggleEmail: this.toggleEmail,
              ssoDisabled: this.users?.ssoDisabled,
              employeesNumbers,
            },
          });
          return data;
        } catch (error: any) {
          // The SDK proxy can reject with a string or an object; normalise before matching.
          const message = typeof error === "string" ? error : (error?.message ?? error?.data?.message ?? "");
          const alreadyInUse = message.toLowerCase().includes("already in use");

          if (isEmailMode) {
            this.emailsListError = alreadyInUse
              ? "Adresse e-mail déjà utilisé"
              : "Une erreur est survenue lors de l'ajout";
          } else {
            this.employeesNumberListError = alreadyInUse
              ? "Matricule déjà utilisé"
              : "Une erreur est survenue lors de l'ajout";
          }
          return undefined;
        }
      }
    },

    extractEmailsFromText(text: string) {
      return text.match(
        // eslint-disable-next-line no-control-regex
        /(?:[a-z0-9+!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0B\x0C\x0E-\x1F\x21\x23-\x5B\x5D-\x7F]|\\[\x01-\x09\x0B\x0C\x0E-\x7F])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0B\x0C\x0E-\x1F\x21-\x5A\x53-\x7F]|\\[\x01-\x09\x0B\x0C\x0E-\x7F])+)\])/gi,
      );
    },
  },
});
